import { useMemo, useState } from 'react'

import { buildProjectDetailsUpdates } from '@util/project-util'

import { useAuthenticatedUser } from '../../../../auth/hooks/useAuthenticatedUser'
import type { HoofdleidingProject } from '../../../../hoofdleiding/project/types/HoofdleidingProject'
import type { GeblokkeerdeWeek } from '../../../../planning/types/GeblokkeerdeWeek'
import type { BijlageTemplate } from '../../../../templates/types/BijlageTemplate'
import { useHoofdleidingContextLists } from '../../../../valuelists/hooks/useHoofdleidingContextLists'
import { useHoofdleidingValuelists } from '../../../../valuelists/hooks/useHoofdleidingValuelists'
import { useCreateProject } from '../../hooks/useCreateProject'
import { useSaveEvent } from '../../hooks/useSaveEvent'

import { CreateEditProjectForm } from './CreateEditProjectForm'

export const CreateEditProjectDialog = ({
  projectInfo,
  handleClose,
  type,
  geblokkeerdeWeken,
  bijlagetemplates,
}: {
  projectInfo?: Partial<HoofdleidingProject>
  type: 'create' | 'edit'
  handleClose: () => void
  geblokkeerdeWeken: GeblokkeerdeWeek[]
  bijlagetemplates?: BijlageTemplate[]
}) => {
  const { deelnemer, organisatie, canCreateWijzigenGeschovenNaarAannemer } =
    useAuthenticatedUser()

  const { mutate: saveEvent, isPending: isPendingUpdate } = useSaveEvent()
  const { mutate: createProject, isPending: isPendingCreate } =
    useCreateProject()

  const [gebiedscode, setGebiedscode] = useState(projectInfo?.gebiedscode)

  const isUpdateProject = type === 'edit'

  // Het project wordt aangemaakt op naam van de organisatie van de gebruiker,
  // bij een bestaand project blijft de verantwoordelijke van dat project gelden
  const verantwoordelijke =
    (isUpdateProject && projectInfo?.verantwoordelijken?.[0]) || organisatie

  const {
    data: { gemeenten, typeWerkTypes, gebiedenForVerantwoordelijke },
  } = useHoofdleidingValuelists()
  const {
    data: { aannemers, labels, netbeheerderDisciplines },
  } = useHoofdleidingContextLists({ gebiedscode, verantwoordelijke })

  // Het gebied van een bestaand project kan niet gewijzigd worden en hoeft dus
  // niet in de gebieden van de gebruiker voor te komen
  const gebieden = useMemo(
    () =>
      isUpdateProject && projectInfo?.gebiedscode
        ? [
            {
              code: projectInfo.gebiedscode,
              label: projectInfo.gebiedscodeLabel || projectInfo.gebiedscode,
            },
          ]
        : gebiedenForVerantwoordelijke,
    [isUpdateProject, projectInfo, gebiedenForVerantwoordelijke],
  )
  const deelnemerNetbeheerderDisciplines = useMemo(
    () =>
      deelnemer
        ? netbeheerderDisciplines.filter(
            ({ netbeheerderLabel }) => netbeheerderLabel === deelnemer,
          )
        : netbeheerderDisciplines,
    [netbeheerderDisciplines, deelnemer],
  )

  const handleSubmit = (request: Partial<HoofdleidingProject>) => {
    if (!isUpdateProject) {
      createProject({ project: request }, { onSuccess: () => handleClose() })
      return
    }

    // An existing project always has an aanvraagID
    /* v8 ignore next -- @preserve */
    if (!request.aanvraagID) return

    saveEvent(
      {
        type: 'hoofdleiding_details',
        id: request.aanvraagID,
        data: buildProjectDetailsUpdates({
          baseProject: projectInfo,
          updatedProject: request,
        }),
        source: 'manual',
      },
      { onSuccess: () => handleClose() },
    )
  }

  return (
    <CreateEditProjectForm
      type={type}
      projectInfo={projectInfo}
      options={{
        gemeenten,
        typeWerkTypes,
        gebieden,
        aannemers,
        labels,
        netbeheerderDisciplines: deelnemerNetbeheerderDisciplines,
      }}
      geblokkeerdeWeken={geblokkeerdeWeken}
      bijlagetemplates={bijlagetemplates}
      canCreateWijzigenGeschovenNaarAannemer={
        canCreateWijzigenGeschovenNaarAannemer
      }
      isPending={isPendingUpdate || isPendingCreate}
      onGebiedscodeChange={setGebiedscode}
      onSubmit={handleSubmit}
      handleClose={handleClose}
    />
  )
}
